import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, Package, Search, Filter, Tag, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getContract, getContractAliases, createContractAlias, updateContractAlias, deleteContractAlias, getProducts } from '../../config/api';

const ContractAliases = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAlias, setEditingAlias] = useState(null);
  const [showProductSelector, setShowProductSelector] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    productId: '',
    aliasName: '',
    aliasType: 'INTERNAL',
    notes: ''
  });

  // Fetch contract details
  const { data: contract, isLoading: contractLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => getContract(id),
    enabled: !!id
  });

  // Fetch contract aliases
  const { data: aliasesData, isLoading: aliasesLoading } = useQuery({
    queryKey: ['contract-aliases', id],
    queryFn: () => getContractAliases(id),
    enabled: !!id
  });

  // Fetch all products for selection
  const { data: allProductsData } = useQuery({
    queryKey: ['products'],
    queryFn: getProducts,
    enabled: showProductSelector
  });

  const contractAliases = aliasesData?.aliases || [];
  const allProducts = allProductsData?.products || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => createContractAlias(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-aliases', id] });
      toast.success('Alias added successfully');
      setShowAddForm(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to add alias');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ aliasId, data }) => updateContractAlias(id, aliasId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-aliases', id] });
      toast.success('Alias updated successfully');
      setEditingAlias(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update alias');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (aliasId) => deleteContractAlias(id, aliasId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-aliases', id] });
      toast.success('Alias deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete alias');
    }
  });

  const resetForm = () => {
    setFormData({
      productId: '',
      aliasName: '',
      aliasType: 'INTERNAL',
      notes: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingAlias) {
      updateMutation.mutate({ aliasId: editingAlias.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (alias) => {
    setEditingAlias(alias);
    setFormData({
      productId: alias.productId,
      aliasName: alias.aliasName || '',
      aliasType: alias.aliasType || 'INTERNAL',
      notes: alias.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = (aliasId) => {
    if (window.confirm('Are you sure you want to delete this alias?')) {
      deleteMutation.mutate(aliasId);
    }
  };

  const handleProductSelect = (product) => {
    setFormData({ ...formData, productId: product.id });
    setShowProductSelector(false);
  };

  const filteredAliases = contractAliases.filter(alias => {
    const matchesSearch = !searchTerm || 
      alias.aliasName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alias.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alias.product?.ndc?.includes(searchTerm);
    
    const matchesType = filterType === 'ALL' || alias.aliasType === filterType;
    
    return matchesSearch && matchesType;
  });

  const availableProducts = allProducts.filter(product => 
    !contractAliases.some(alias => alias.productId === product.id)
  );

  if (contractLoading || aliasesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contract not found</h2>
          <button
            onClick={() => navigate('/app/contracts')}
            className="text-primary-600 hover:text-primary-700"
            title="Back to Contracts"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/app/contracts')}
            className="text-gray-400 hover:text-gray-600"
            title="Back to Contracts"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Contract Aliases</h1>
            <p className="text-sm text-gray-500">
              {contract.number} - {contract.description}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          title="Add Alias"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Alias
        </button>
      </div>

      {/* Contract Summary */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Contract Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Type</p>
            <p className="font-medium">{contract.type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <p className="font-medium">{contract.status}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Effective Period</p>
            <p className="font-medium">
              {new Date(contract.effectiveFrom).toLocaleDateString()} - {new Date(contract.effectiveTo).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingAlias ? 'Edit Alias' : 'Add New Alias'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product
                </label>
                {editingAlias ? (
                  <input
                    type="text"
                    value={editingAlias.product?.name || 'Unknown Product'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Select product..."
                      value={formData.productId ? 
                        allProducts.find(p => p.id === formData.productId)?.name || 'Unknown Product' : 
                        ''
                      }
                      onClick={() => setShowProductSelector(true)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:border-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowProductSelector(true)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Search products"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alias Name
                </label>
                <input
                  type="text"
                  value={formData.aliasName}
                  onChange={(e) => setFormData({ ...formData, aliasName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter alias name..."
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alias Type
                </label>
                <select
                  value={formData.aliasType}
                  onChange={(e) => setFormData({ ...formData, aliasType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="INTERNAL">Internal</option>
                  <option value="CUSTOMER">Customer</option>
                  <option value="SUPPLIER">Supplier</option>
                  <option value="REGULATORY">Regulatory</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="Optional notes about this alias..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingAlias(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                title="Cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending || (!editingAlias && !formData.productId)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                title={editingAlias ? 'Update Alias' : 'Add Alias'}
              >
                {editingAlias ? 'Update Alias' : 'Add Alias'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Product Selector Modal */}
      {showProductSelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Select Product</h3>
                <button
                  onClick={() => setShowProductSelector(false)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {availableProducts.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No available products to add</p>
                ) : (
                  <div className="space-y-2">
                    {availableProducts.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleProductSelect(product)}
                        className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-primary-300 transition-colors"
                        title="Select product"
                      >
                        <div className="font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">
                          NDC: {product.ndc || 'N/A'} | Form: {product.form || 'N/A'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search aliases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Types</option>
            <option value="INTERNAL">Internal</option>
            <option value="CUSTOMER">Customer</option>
            <option value="SUPPLIER">Supplier</option>
            <option value="REGULATORY">Regulatory</option>
          </select>
        </div>
      </div>

      {/* Aliases Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Contract Aliases ({filteredAliases.length})</h3>
        </div>
        
        {filteredAliases.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No aliases found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterType !== 'ALL' 
                ? 'Try adjusting your search or filters.'
                : 'Get started by adding your first alias.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alias Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alias Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAliases.map((alias) => (
                  <tr key={alias.id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {alias.product?.name || 'Unknown Product'}
                        </div>
                        <div className="text-sm text-gray-500">
                          NDC: {alias.product?.ndc || 'No NDC'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {alias.aliasName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          alias.aliasType === 'INTERNAL' ? 'bg-blue-100 text-blue-800' :
                          alias.aliasType === 'CUSTOMER' ? 'bg-green-100 text-green-800' :
                          alias.aliasType === 'SUPPLIER' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {alias.aliasType}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {alias.notes || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(alias)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(alias.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractAliases;
